<script setup lang="ts">
// stop, start, change-build, refresh-data, teardown (and delete). One at a time: an action in flight
// disables the others and is named. Refresh-data, teardown and delete need the name typed (FR-30).
import { computed, ref, watch } from 'vue';
import { must } from '../api/client';
import type { ActionRequest, ActionType, Backup, Build, Environment } from '../api/types';
import { useContext } from '../context';
import { ACTIONS, actionInfo, availability, localPendingSettled, needsConfirmation } from '../actions/guards';
import { usePendingActions } from '../stores/actions';
import { useSession } from '../stores/session';
import { validate, type FieldError } from '../api/validate';
import { missingBuild } from '../forms/build';
import ConfirmByName from './ConfirmByName.vue';
import BuildPicker from './BuildPicker.vue';
import ProblemAlert from './ProblemAlert.vue';
import Icon from './Icon.vue';

// zone: 'toolbar' = stop, start, change-build (the page header); 'danger' = refresh-data, teardown, delete; both when absent.
// formTo: a selector the change-build form is moved to (the page header is too narrow for it); in place when absent.
const props = defineProps<{ env: Environment; zone?: 'toolbar' | 'danger'; formTo?: string }>();
const emit = defineEmits<{ changed: [] }>();

const { api } = useContext();
const pending = usePendingActions();
const session = useSession();

const error = ref<unknown>();
const accepted = ref<string>();
const confirming = ref<ActionType | 'delete'>();
const changeBuildOpen = ref(false);
const newBuild = ref<Build>();
// which build change-build replaces: the shop's, or a service's own zip (plan.md M7)
const buildTarget = ref<'peaksolution' | 'cashdesk' | 'ticketservice' | 'contingentservice'>('peaksolution');
const buildErrors = ref<FieldError[]>([]);
const refreshBackup = ref('latest');
const backups = ref<Backup[]>([]);
// refresh-data may switch the data to another source, another customer's too: any the caller may order
const refreshSource = ref('');
// spec.db.preserveConfig, written with the request: keep the environment's own core_config_data
const refreshPreserve = ref(false);
const own = computed(() => `${props.env.source.customer}/${props.env.source.environment}`);
const sources = computed(() => {
  const all = (session.me?.entitlements ?? []).flatMap((e) => e.environments.map((x) => `${e.customer}/${x}`));
  return [...new Set([own.value, ...all])].sort();
});

function loadBackups(source: string) {
  const [customer, environment] = source.split('/') as [string, string];
  backups.value = [];
  refreshBackup.value = 'latest';
  api
    .GET('/sources/{customer}/{environment}/backups', { params: { path: { customer, environment }, query: { limit: 50 } } })
    .then((r) => (backups.value = r.data?.items ?? []))
    .catch(() => {});
}
watch(refreshSource, (s) => { if (s) loadBackups(s); });

const local = computed(() => pending.byEnv[props.env.name]);
watch(
  () => props.env,
  (env) => {
    const l = local.value;
    if (l && localPendingSettled(l, env, l.actionId, Date.now())) pending.clear(env.name);
  },
);

const avail = computed(() => availability(props.env, local.value));
const anyBlocked = computed(() => !!avail.value.inFlight || !!props.env.tornDown || !!props.env.deleting);

/**
 * Why the caller's role forbids it (the API decides the same; docs/proposal-environment-access.md): teardown and
 * delete are the owner's, the rest an operator's, and those need the data entitlement beside the role.
 */
function roleBlocked(type: ActionType | 'delete'): string | undefined {
  const role = props.env.yourRole;
  if (role === 'admin') return undefined;
  if (type === 'teardown' || type === 'delete') return role === 'owner' ? undefined : 'Only the owner (or an admin) removes the environment';
  if (role !== 'owner' && role !== 'operator') return 'Needs an operator role on the access list';
  if (!props.env.dataAccess) return `You are not entitled to the raw data of ${own.value}`;
  return undefined;
}

async function send(type: ActionType, extra: Partial<ActionRequest> = {}) {
  error.value = undefined;
  accepted.value = undefined;
  pending.begin(props.env.name, type);
  try {
    const a = must(await api.POST('/environments/{name}/actions', { params: { path: { name: props.env.name } }, body: { type, ...extra } }));
    pending.accepted(props.env.name, a.actionId);
    accepted.value = `${type} requested${a.actionId ? ` (id ${a.actionId})` : ''}${a.order ? `, order ${a.order}` : ''}. The operator performs it now; follow it below.`;
    changeBuildOpen.value = false;
    emit('changed');
  } catch (e) {
    pending.clear(props.env.name);
    error.value = e;
  }
}

async function remove(confirm: string) {
  error.value = undefined;
  pending.begin(props.env.name, 'delete');
  try {
    must(await api.DELETE('/environments/{name}', { params: { path: { name: props.env.name }, query: { confirm } } }));
    accepted.value = 'Deletion requested; the environment disappears once its teardown has run.';
    emit('changed');
  } catch (e) {
    pending.clear(props.env.name);
    error.value = e;
  }
}

async function click(type: ActionType) {
  if (type === 'change-build') {
    changeBuildOpen.value = !changeBuildOpen.value;
    return;
  }
  if (type === 'refresh-data') {
    refreshPreserve.value = !!props.env.spec?.preserveConfig;
    if (refreshSource.value === own.value) loadBackups(own.value);
    else refreshSource.value = own.value;
  }
  if (needsConfirmation(type)) confirming.value = type;
  else await send(type);
}

async function confirmed(typed: string) {
  const type = confirming.value;
  confirming.value = undefined;
  if (type === 'delete') await remove(typed);
  else if (type === 'refresh-data') {
    const [customer, environment] = refreshSource.value.split('/') as [string, string];
    await send(type, { confirm: typed, backupId: refreshBackup.value, preserveConfig: refreshPreserve.value,
      ...(refreshSource.value !== own.value ? { source: { customer, environment } } : {}) });
  }
  else if (type) await send(type, { confirm: typed });
}

async function submitBuild() {
  const t = buildTarget.value;
  const errs =
    t === 'peaksolution'
      ? [...validate(newBuild.value, 'Build', 'build'), ...missingBuild(newBuild.value)]
      : [...(newBuild.value ? validate(newBuild.value.base, 'Artefact', 'build.base') : []), ...missingBuild(newBuild.value)];
  buildErrors.value = errs;
  if (errs.length || !newBuild.value) return;
  await send('change-build', t === 'peaksolution' ? { peaksolution: newBuild.value } : { [t]: newBuild.value.base });
}

const showToolbar = computed(() => props.zone !== 'danger');
const showDanger = computed(() => props.zone !== 'toolbar');
const safe = ACTIONS.filter((a) => !a.destructive);
const destructive = ACTIONS.filter((a) => a.destructive);
/** One sentence when the caller's role keeps them from the everyday actions (the same reason holds for all three). */
const toolbarWhy = computed(() => roleBlocked('stop'));
const deleteBlocked = computed(() => {
  if (props.env.deleting) return `${props.env.name} is being deleted`;
  if (anyBlocked.value && !props.env.tornDown) return `${avail.value.inFlight?.type ?? 'An action'} is in flight; one action at a time`;
  return roleBlocked('delete');
});

const confirmText = computed(() => {
  const t = confirming.value;
  if (t === 'delete') return { title: `Delete ${props.env.name}?`, description: 'Runs the ordered teardown and then removes the environment and its record.', label: 'Delete' };
  if (!t) return { title: '', description: '', label: '' };
  const i = actionInfo(t);
  return { title: `${i.label}: ${props.env.name}?`, description: i.description, label: i.label };
});
</script>

<template>
  <div class="action-panel">
    <section v-if="showToolbar" aria-labelledby="actions-h" :class="zone ? '' : 'card'">
      <h2 id="actions-h" class="sr-only">Actions</h2>
      <p v-if="avail.inFlight" class="notice" role="status" data-testid="in-flight" style="margin-bottom: 0.75rem">
        <strong>{{ avail.inFlight.type }}</strong> is in flight<template v-if="avail.inFlight.startedAt"> since {{ avail.inFlight.startedAt }}</template><template v-if="avail.inFlight.id"> (id <code>{{ avail.inFlight.id }}</code>)</template>
        <template v-if="avail.inFlight.local"> — sent, waiting for the API</template>. Other actions are disabled until it finishes.
      </p>
      <p v-else-if="env.tornDown" class="info" style="margin-bottom: 0.75rem">This environment was torn down; the object is only the record.</p>

      <div class="actions-row" role="group" aria-label="Lifecycle actions">
        <button
          v-for="a in safe"
          :key="a.type"
          type="button"
          :class="{ primary: a.type === 'change-build' }"
          :disabled="!!avail.blocked[a.type] || !!roleBlocked(a.type)"
          :title="avail.blocked[a.type] ?? roleBlocked(a.type) ?? a.description"
          :aria-expanded="a.type === 'change-build' ? changeBuildOpen : undefined"
          :data-testid="`action-${a.type}`"
          @click="click(a.type)"
        >
          <Icon v-if="a.type === 'stop'" name="stop" /><Icon v-else-if="a.type === 'change-build'" name="build" />{{ a.label }}
        </button>
      </div>
      <p v-if="toolbarWhy" class="why" style="margin-top: 6px"><Icon name="lock" :size="13" /> {{ toolbarWhy }}.</p>

      <Teleport defer :to="formTo ?? 'body'" :disabled="!formTo">
      <form v-if="changeBuildOpen && !avail.blocked['change-build']" class="card" :style="formTo ? '' : 'margin-top: 1rem'" style="display: flex; flex-direction: column; gap: 1rem" novalidate @submit.prevent="submitBuild">
        <h3 style="margin: 0">Change build</h3>
        <div class="field">
          <label for="cb-target">Build to change</label>
          <select id="cb-target" v-model="buildTarget" data-testid="cb-target">
            <option value="peaksolution">Peaksolution</option>
            <option value="cashdesk">Cashdesk</option>
            <option value="ticketservice">Ticket service</option>
            <option value="contingentservice">Contingent service</option>
          </select>
          <p class="hint">Only that build is replaced; the database and the other builds are kept.</p>
        </div>
        <BuildPicker :key="buildTarget" v-model="newBuild" id-prefix="cb" :project="buildTarget" :errors="buildErrors" />
        <div class="actions-row">
          <button type="submit" class="primary" data-testid="cb-submit">Change build</button>
          <button type="button" @click="changeBuildOpen = false">Cancel</button>
        </div>
      </form>
      </Teleport>
    </section>

    <section v-if="showDanger" class="card danger-zone" aria-labelledby="danger-h" :style="zone ? '' : 'margin-top: 1rem'">
      <h2 id="danger-h">Destructive actions</h2>
      <p class="muted" style="margin: 0 0 0.5rem">Each one asks you to type <code>{{ env.name }}</code>. Operators may refresh data; only the owner or an admin tears down or deletes.</p>
      <div v-for="a in destructive" :key="a.type" class="danger-row">
        <div class="grow">
          <div style="font-weight: 600">{{ a.label }}</div>
          <div class="muted" style="font-size: 13px">{{ a.type === 'refresh-data' ? 'Drops the database and restores a backup, from this data source or another one. Hosts, builds and access stay.' : a.description }}</div>
          <div v-if="avail.blocked[a.type] ?? roleBlocked(a.type)" class="why"><Icon name="lock" :size="13" /> {{ avail.blocked[a.type] ?? roleBlocked(a.type) }}</div>
        </div>
        <button type="button" class="danger" :disabled="!!avail.blocked[a.type] || !!roleBlocked(a.type)" :data-testid="`action-${a.type}`" @click="click(a.type)">{{ a.label }}…</button>
      </div>
      <div class="danger-row">
        <div class="grow">
          <div style="font-weight: 600">Delete environment</div>
          <div class="muted" style="font-size: 13px">Runs the teardown and removes the environment and its record.</div>
          <div v-if="deleteBlocked" class="why"><Icon name="lock" :size="13" /> {{ deleteBlocked }}</div>
        </div>
        <button type="button" class="danger-solid" :disabled="!!deleteBlocked" :title="deleteBlocked ?? 'Runs the teardown and removes the record'" data-testid="action-delete" @click="confirming = 'delete'">Delete…</button>
      </div>
    </section>

    <p v-if="accepted" class="info" role="status" style="margin-top: 0.75rem">{{ accepted }}</p>
    <div v-if="error" style="margin-top: 0.75rem"><ProblemAlert :error="error" context="The API refused the action" /></div>

    <ConfirmByName v-if="showDanger" :open="!!confirming" :name="env.name" :title="confirmText.title" :description="confirmText.description" :confirm-label="confirmText.label" @confirm="confirmed" @cancel="confirming = undefined">
      <div v-if="confirming === 'refresh-data'" class="field">
        <label for="refresh-source">Data from</label>
        <select id="refresh-source" v-model="refreshSource" data-testid="refresh-source">
          <option v-for="s in sources" :key="s" :value="s">{{ s }}{{ s === own ? ' (its current source)' : '' }}</option>
        </select>
        <p v-if="refreshSource !== own" class="notice" style="font-size: 12.5px">Switches {{ env.name }} from <code>{{ own }}</code> to the data of <code>{{ refreshSource }}</code>; its hosts, builds and access stay. While it runs, members need the entitlement to both.</p>
      </div>
      <div v-if="confirming === 'refresh-data'" class="field">
        <label for="refresh-backup">Backup to restore</label>
        <select id="refresh-backup" v-model="refreshBackup" data-testid="refresh-backup">
          <option value="latest">latest (the newest when the order is placed)</option>
          <option v-for="b in backups" :key="b.backupId" :value="b.backupId">{{ b.takenAt }} · {{ b.flavour }} · {{ b.backupId }}</option>
        </select>
      </div>
      <div v-if="confirming === 'refresh-data'" class="field">
        <label><input v-model="refreshPreserve" type="checkbox" data-testid="refresh-preserve-config" /> Keep this environment's configuration (core_config_data)</label>
        <p class="muted" style="font-size: 12.5px; margin: 0.25rem 0 0">The whole table is stored before the wipe and applied again first, then sanitize, wire and the data checks run over it. Needs the data on disk to be prepared and the new backup to have the same crypt key; otherwise the refresh stops before anything is wiped.</p>
      </div>
    </ConfirmByName>
  </div>
</template>
